import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from './firebase/FirebaseProvider';
import { UserService } from './services/userService';
import TopicForm from './packages/topic-form';
import TopicList from './packages/topic-list';
import NewsItemsList from './packages/news-items-list';
import SavedItemsList from './packages/saved-items-list/SavedItemsList';
import TabNavigation, { TabId, Tab } from './packages/tab-navigation';
import LoginPopup from './packages/login-popup';
import UserPage from './packages/user-page';
import { NewsItemsSkeletonList } from './components/SkeletonLoader';
import { Topic } from './services/topicService';
import './App.css';

function App() {
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'info'} | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const showNotification = (message: string, type: 'success' | 'info' = 'success') => {
    setNotification({ message, type });
    // Auto-hide after 2 seconds
    setTimeout(() => {
      setNotification(null);
    }, 2000);
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // Always save/update user data to ensure role is set correctly
          const userData = await UserService.saveOrUpdateUser(firebaseUser);
          setUser(userData);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email
          });
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sync selectedTopic with URL parameter
  useEffect(() => {
    const pathMatch = location.pathname.match(/^\/feed\/(.+)$/);
    if (pathMatch) {
      const topicNameFromUrl = pathMatch[1];
      const topicDisplay = topicNameFromUrl.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      // Create a topic object from URL parameter
      const topicFromUrl: Topic = {
        id: topicNameFromUrl,
        topicValue: topicDisplay,
        type: 'COMMON', // We'll assume COMMON for URL-based topics
        status: 'ACTIVE',
        createdAt: new Date()
      };
      
      // Only update if different from current selection
      if (!selectedTopic || selectedTopic.topicValue !== topicDisplay) {
        setSelectedTopic(topicFromUrl);
      }
    } else if (location.pathname === '/') {
      // Clear selected topic when on main feed
      if (selectedTopic) {
        setSelectedTopic(null);
      }
    }
  }, [location.pathname, selectedTopic]);

  // Determine active tab based on current route
  const getActiveTab = (): TabId => {
    if (location.pathname === '/topics') {
      return 'topics';
    }
    if (location.pathname === '/saved') {
      return 'saved';
    }
    return 'news-items'; // Default to feed for both / and /feed/* routes
  };

  const handleTopicClick = (topic: Topic) => {
    setSelectedTopic(topic);
    const topicName = topic.topicValue.trim().toLowerCase().replace(/\s+/g, '-');
    navigate(`/feed/${topicName}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToAllNews = () => {
    setSelectedTopic(null);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (tabId: TabId) => {
    // Always scroll to top when changing tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Handle user page navigation (accessed via profile button)
    if (tabId === 'user') {
      navigate('/user');
      return;
    }
    
    // Clear selected topic when switching tabs
    if (tabId === 'topics') {
      setSelectedTopic(null);
      navigate('/topics');
    } else if (tabId === 'saved') {
      setSelectedTopic(null);
      navigate('/saved');
    } else {
      // If clicking on Feed tab (news-items), always clear selected topic
      setSelectedTopic(null);
      navigate('/');
    }
  };

  const handleLoginSuccess = (userData: any) => {
    setUser(userData);
    setShowLoginPopup(false);
  };

  const handleLogout = () => {
    setUser(null);
    navigate('/'); // Redirect to home after logout
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoginClick = () => {
    setShowLoginPopup(true);
  };

  // Create tabs array - only show tabs if authenticated
  const tabs: Tab[] = [];

  // Add feed, saved and topics tabs only if authenticated
  if (user) {
    tabs.push({
      id: 'news-items',
      label: 'Feed'
    });
    tabs.push({
      id: 'saved',
      label: 'Saved'
    });
    tabs.push({
      id: 'topics',
      label: 'Topics'
    });
  }

  const renderTabContent = () => {
    if (authLoading) {
      return (
        <div className="news-items-list">
          <NewsItemsSkeletonList count={5} />
        </div>
      );
    }

    return (
      <Routes>
        <Route path="/topics" element={
          user ? (
            <div id="topics-panel" role="tabpanel" aria-labelledby="topics-tab">
              <TopicForm user={user} />
              <TopicList user={user} onTopicClick={handleTopicClick} />
            </div>
          ) : (
            <div className="auth-required">
              <h2>Authentication Required</h2>
              <p>Please sign in to access your topics.</p>
              <button className="auth-cta-button" onClick={handleLoginClick}>
                Sign In
              </button>
            </div>
          )
        } />
        <Route path="/user" element={
          user ? (
            <div id="user-panel" role="tabpanel" aria-labelledby="user-tab">
              <UserPage user={user} onLogout={handleLogout} />
            </div>
          ) : (
            <div className="auth-required">
              <h2>Authentication Required</h2>
              <p>Please sign in to access your account.</p>
              <button className="auth-cta-button" onClick={handleLoginClick}>
                Sign In
              </button>
            </div>
          )
        } />
        <Route path="/feed/:topicName" element={
          <div id="news-items-panel" role="tabpanel" aria-labelledby="news-items-tab">
            <NewsItemsList 
              selectedTopic={selectedTopic}
              onBackToAllNews={handleBackToAllNews}
              onTopicClick={handleTopicClick}
              user={user}
              onShowNotification={showNotification}
              selectedTab="feed"
            />
          </div>
        } />
        <Route path="/saved" element={
          <div id="saved-panel" role="tabpanel" aria-labelledby="saved-tab">
            <SavedItemsList 
              selectedTopic={selectedTopic}
              onBackToAllNews={handleBackToAllNews}
              onTopicClick={handleTopicClick}
              user={user}
              onShowNotification={showNotification}
            />
          </div>
        } />
        <Route path="/" element={
          <div id="news-items-panel" role="tabpanel" aria-labelledby="news-items-tab">
            <NewsItemsList 
              selectedTopic={selectedTopic}
              onBackToAllNews={handleBackToAllNews}
              onTopicClick={handleTopicClick}
              user={user}
              onShowNotification={showNotification}
              selectedTab="feed"
            />
          </div>
        } />
      </Routes>
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="site-logo">MT</div>
        <TabNavigation 
          tabs={tabs}
          activeTab={getActiveTab()}
          onTabChange={handleTabChange}
          user={user}
          onLoginClick={handleLoginClick}
        />
      </header>
      
      <main className="App-main">
        <div className="container">
          {renderTabContent()}
        </div>
      </main>

      <LoginPopup 
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Global Notification */}
      {notification && (
        <div className={`global-notification global-notification-${notification.type}`}>
          NOTIFICATION: {notification.message}
        </div>
      )}
    </div>
  );
}

export default App;